import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import {
    Box,
    Card,
    CardActionArea,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DownloadIcon from '@mui/icons-material/Download';
import { formatDate } from '../../../utils';
import { formatFileSize } from '../services/closeoutDocsService';
import { getFileIcon } from '../utils/fileIconUtils';

/**
 * UnifiedGrid Component
 * Displays folders and documents in a responsive grid layout
 * Folders appear first, followed by documents
 * Mobile: Google Drive style - short horizontal rows
 * Desktop: Grid cards
 *
 * @param {Array} folders - Array of folder objects
 * @param {Array} documents - Array of document objects
 * @param {string} sortBy - Current sort field ('name' or 'date')
 * @param {string} sortOrder - Current sort order ('asc' or 'desc')
 * @param {Function} onSortChange - Callback when sort changes
 * @param {Function} onFolderClick - Callback when a folder is clicked
 * @param {Function} onDocumentClick - Callback when a document is clicked
 * @param {Function} onFolderRename - Callback for folder rename
 * @param {Function} onFolderDelete - Callback for folder delete
 * @param {Function} onFolderMove - Callback for folder move
 * @param {Function} onDocumentRename - Callback for document rename
 * @param {Function} onDocumentDownload - Callback for document download
 * @param {Function} onDocumentDelete - Callback for document delete
 * @param {Function} onDocumentMove - Callback for document move
 */
const UnifiedGrid = ({
    folders = [],
    documents = [],
    onFolderClick,
    onDocumentClick,
    onFolderRename,
    onFolderDelete,
    onFolderMove,
    onDocumentRename,
    onDocumentDownload,
    onDocumentDelete,
    onDocumentMove,
    canWrite = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState(null); // 'folder' or 'document'

    const handleMenuOpen = (event, item, type) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
        setSelectedItemType(type);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedItem(null);
        setSelectedItemType(null);
    };

    const handleRename = () => {
        if (selectedItem && selectedItemType === 'folder' && onFolderRename) {
            onFolderRename(selectedItem);
        } else if (selectedItem && selectedItemType === 'document' && onDocumentRename) {
            onDocumentRename(selectedItem);
        }
        handleMenuClose();
    };

    const handleDownload = () => {
        if (selectedItem && selectedItemType === 'document' && onDocumentDownload) {
            onDocumentDownload(selectedItem);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedItem) {
            if (selectedItemType === 'folder' && onFolderDelete) {
                onFolderDelete(selectedItem);
            } else if (selectedItemType === 'document' && onDocumentDelete) {
                onDocumentDelete(selectedItem);
            }
        }
        handleMenuClose();
    };

    const handleMove = () => {
        if (selectedItem) {
            if (selectedItemType === 'folder' && onFolderMove) {
                onFolderMove(selectedItem);
            } else if (selectedItemType === 'document' && onDocumentMove) {
                onDocumentMove(selectedItem);
            }
        }
        handleMenuClose();
    };

    if (folders.length === 0 && documents.length === 0) {
        return null;
    }

    const renderFolderCard = (folder) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={folder.folderId}>
            <Card
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.primary.main,
                    },
                }}
            >
                <CardActionArea
                    onClick={() => onFolderClick && onFolderClick(folder.folderId)}
                    sx={{
                        height: isMobile ? 64 : 140,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        p: isMobile ? 1.5 : 1.25,
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    {isMobile ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                gap: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <FolderIcon
                                sx={{
                                    fontSize: 32,
                                    color: theme.palette.primary.main,
                                    flexShrink: 0,
                                }}
                            />
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {folder.folderName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    }}
                                >
                                    {folder.documentCount || 0} items
                                </Typography>
                            </Box>
                            {canWrite && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, folder, 'folder')}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    mb: 0.75,
                                    width: '100%',
                                }}
                            >
                                <FolderIcon
                                    sx={{
                                        fontSize: 28,
                                        color: theme.palette.primary.main,
                                    }}
                                />
                                {canWrite && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, folder, 'folder')}
                                        sx={{ mt: -0.5, mr: -0.5, p: 0.5 }}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.3,
                                }}
                            >
                                {folder.folderName}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    mb: 0.25,
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                }}
                            >
                                {folder.documentCount || 0} doc
                                {folder.documentCount !== 1 ? 's' : ''},{' '}
                                {folder.subfolderCount || 0} folder
                                {folder.subfolderCount !== 1 ? 's' : ''}
                            </Typography>

                            {folder.createdAt && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.7rem',
                                        mt: 'auto',
                                        pt: 0.5,
                                    }}
                                >
                                    {formatDate(folder.createdAt)}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardActionArea>
            </Card>
        </Grid>
    );

    const renderDocumentCard = (document) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={document.documentId}>
            <Card
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        boxShadow: theme.shadows[2],
                        borderColor: theme.palette.primary.main,
                    },
                }}
            >
                <CardActionArea
                    onClick={() => onDocumentClick && onDocumentClick(document)}
                    sx={{
                        height: isMobile ? 64 : 140,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        p: isMobile ? 1.5 : 1.25,
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    {isMobile ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                gap: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{ flexShrink: 0 }}>{getFileIcon(document.fileExtension)}</Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={document.originalFilename}
                                >
                                    {document.originalFilename}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    }}
                                >
                                    {document.fileSizeBytes
                                        ? formatFileSize(document.fileSizeBytes)
                                        : 'Unknown size'}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, document, 'document')}
                                sx={{ flexShrink: 0 }}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    mb: 0.75,
                                    width: '100%',
                                }}
                            >
                                <Box sx={{ fontSize: 28 }}>
                                    {getFileIcon(document.fileExtension, 'medium')}
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, document, 'document')}
                                    sx={{ mt: -0.5, mr: -0.5, p: 0.5 }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.3,
                                }}
                                title={document.originalFilename}
                            >
                                {document.originalFilename}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    mb: 0.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {document.fileExtension && (
                                    <Chip
                                        label={document.fileExtension.toUpperCase()}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: 20,
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                                {document.fileSizeBytes && (
                                    <Chip
                                        label={formatFileSize(document.fileSizeBytes)}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: 20,
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                            </Box>

                            {document.uploadedAt && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.7rem',
                                        mt: 'auto',
                                        pt: 0.5,
                                    }}
                                >
                                    {formatDate(document.uploadedAt)}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardActionArea>
            </Card>
        </Grid>
    );

    return (
        <>
            <Grid container spacing={1.5} sx={{ width: '100%', margin: 0 }}>
                {/* Render folders first */}
                {folders.map(renderFolderCard)}
                {/* Then documents */}
                {documents.map(renderDocumentCard)}
            </Grid>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {canWrite && (
                    <MenuItem onClick={handleRename}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                )}

                {selectedItemType === 'document' && (
                    <MenuItem onClick={handleDownload}>
                        <ListItemIcon>
                            <DownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Download</ListItemText>
                    </MenuItem>
                )}

                {canWrite && (
                    <MenuItem onClick={handleMove}>
                        <ListItemIcon>
                            <DriveFileMoveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move</ListItemText>
                    </MenuItem>
                )}

                {canWrite && (
                    <MenuItem onClick={handleDelete} sx={{ color: theme.palette.error.main }}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default UnifiedGrid;
